import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Modal, message, Space, Typography, Divider, Tag, Spin, Alert, Tooltip } from 'antd';
import { SettingOutlined, PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, DatabaseOutlined, GlobalOutlined, UserOutlined, LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import styles from './SSW.module.css';

const { Title, Text, Paragraph } = Typography;

interface SswConfig {
  dominio?: string;
  cpf?: string;
  usuario?: string;
  senha?: string;
  baseUrl?: string;
}

interface SswStatus {
  estado: string;
  navegadorId?: string;
  url?: string;
}

const SSWComponent: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sswConfig, setSswConfig] = useState<SswConfig | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBrowserLoading, setIsBrowserLoading] = useState(false);
  const [sswStatus, setSswStatus] = useState<SswStatus | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Load initial configuration
  useEffect(() => {
    loadSswConfig();
  }, []);

  const loadSswConfig = async () => {
    try {
      setLoading(true);
      const config = await nr.getSswConfig();
      setSswConfig(config);
      setConfigLoaded(true);
    } catch (error) {
      setSswConfig(null);
      setConfigLoaded(false);
      message.warning('Nenhuma configuração SSW encontrada. Clique em "Configurar" para começar.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = () => {
    form.setFieldsValue({
      dominio: sswConfig?.dominio || '',
      cpf: sswConfig?.cpf || '',
      usuario: sswConfig?.usuario || '',
      senha: '',
      baseUrl: sswConfig?.baseUrl || 'https://sistema.ssw.inf.br/bin/ssw0422',
    });
    setIsModalOpen(true);
  };

  const handleSaveConfig = async (values: SswConfig) => {
    try {
      setLoading(true);
      const payload = { ...values };
      if (!payload.senha && sswConfig) {
        delete payload.senha;
      }
      await nr.upsertSswConfig(payload);
      message.success('Configuração SSW salva com sucesso!');
      setIsModalOpen(false);
      loadSswConfig();
    } catch (error) {
      message.error(error.message || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleIniciarSsw = async () => {
    try {
      setIsBrowserLoading(true);
      const status = await nr.iniciarSsw();
      setSswStatus(status);
      message.success(`Navegador SSW iniciado: ${status.estado}`);
    } catch (error) {
      message.error(error.message || 'Erro ao iniciar navegador SSW');
    } finally {
      setIsBrowserLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    try {
      const status = await nr.sswStatus();
      setSswStatus(status);
      if (status.estado === 'ativo') {
        message.success('SSW está ativo e funcionando');
      } else {
        message.info(`Status SSW: ${status.estado}`);
      }
    } catch (error) {
      message.error(error.message || 'Erro ao verificar status');
    }
  };

  const handleFecharSsw = async () => {
    try {
      await nr.fecharSsw();
      setSswStatus(null);
      message.success('SSW encerrado com sucesso');
    } catch (error) {
      message.error(error.message || 'Erro ao encerrar SSW');
    }
  };

  const getStatusColor = (estado: string) => {
    switch (estado.toLowerCase()) {
      case 'ativo': return 'success';
      case 'inativo': return 'error';
      case 'iniciando': return 'processing';
      case 'aguardando': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (estado: string) => {
    switch (estado.toLowerCase()) {
      case 'ativo': return 'Ativo';
      case 'inativo': return 'Inativo';
      case 'iniciando': return 'Iniciando';
      case 'aguardando': return 'Aguardando';
      default: return estado;
    }
  };

  return (
    <div className={styles.sswContainer}>
      <Card
        title={
          <Space>
            <DatabaseOutlined style={{ color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0 }}>
              Dashboard de Integrações SSW
            </Title>
          </Space>
        }
        extra={
          <Tooltip title="Recarregar configurações">
            <Button
              icon={<ReloadOutlined />}
              onClick={loadSswConfig}
              loading={loading}
              type="primary"
              ghost
            >
              Atualizar
            </Button>
          </Tooltip>
        }
        className={`${styles.sswCard} ${styles.sswDashboardCard}`}
        style={{ marginBottom: '24px' }}
      >
        <Paragraph type="secondary">
          Gerencie as configurações de integração com o sistema SSW (Serviço de Software de Gestío).
        </Paragraph>
      </Card>

      {!configLoaded && !loading && (
        <Alert
          message="Configuração não encontrada"
          description="Clique no botío 'Configurar' abaixo para adicionar as credenciais de acesso ao SSW."
          type="warning"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: '24px' }}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* Configuração Card */}
        <Card
          title={
            <Space>
              <SettingOutlined />
              <Text>Configuração</Text>
            </Space>
          }
          extra={<Tag color="blue">Obrigatório</Tag>}
          style={{ height: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary">Status da Configuração:</Text>
              <Tag color={sswConfig ? 'success' : 'error'}>
                {sswConfig ? 'Configurado' : 'Não Configurado'}
              </Tag>
            </div>

            {sswConfig && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space>
                    <GlobalOutlined style={{ color: '#1890ff' }} />
                    <Text strong>Domínio:</Text>
                    <Text>{sswConfig.dominio || 'Não definido'}</Text>
                  </Space>
                  <Space>
                    <UserOutlined style={{ color: '#52c41a' }} />
                    <Text strong>Usuário:</Text>
                    <Text>{sswConfig.usuario || 'Não definido'}</Text>
                  </Space>
                  <Space>
                    <LockOutlined style={{ color: '#f5222d' }} />
                    <Text strong>CPF:</Text>
                    <Text>{sswConfig.cpf ? `***${sswConfig.cpf.slice(-3)}` : 'Não definido'}</Text>
                  </Space>
                  <Space>
                    <DatabaseOutlined style={{ color: '#722ed1' }} />
                    <Text strong>URL Base:</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {sswConfig.baseUrl}
                    </Text>
                  </Space>
                </Space>
              </>
            )}

            <Divider style={{ margin: '16px 0' }} />
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={handleOpenEditModal}
              loading={loading}
              block
            >
              {sswConfig ? 'Editar Configuração' : 'Configurar SSW'}
            </Button>
          </Space>
        </Card>

        {/* Status Card */}
        <Card
          title={
            <Space>
              <CheckCircleOutlined />
              <Text>Status do Navegador</Text>
            </Space>
          }
          extra={<Tag color="green">Monitoramento</Tag>}
          style={{ height: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {sswStatus ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <Tag color={getStatusColor(sswStatus.estado)} size="large" style={{ fontSize: '14px', padding: '4px 12px' }}>
                    {getStatusText(sswStatus.estado)}
                  </Tag>
                </div>

                <Divider style={{ margin: '8px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {sswStatus.navegadorId && (
                    <Space>
                      <Text strong>ID do Navegador:</Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {sswStatus.navegadorId}
                      </Text>
                    </Space>
                  )}
                  {sswStatus.url && (
                    <Space>
                      <Text strong>URL:</Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {sswStatus.url}
                      </Text>
                    </Space>
                  )}
                </Space>

                <Divider style={{ margin: '16px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={handleIniciarSsw}
                    loading={isBrowserLoading}
                    block
                    disabled={sswStatus?.estado === 'ativo'}
                  >
                    {sswStatus?.estado === 'ativo' ? 'Navegador Ativo' : 'Iniciar Navegador'}
                  </Button>
                  <Button
                    icon={<CheckCircleOutlined />}
                    onClick={handleCheckStatus}
                    loading={loading}
                    block
                  >
                    Verificar Status
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={handleFecharSsw}
                    loading={isBrowserLoading}
                    block
                    disabled={sswStatus?.estado !== 'ativo'}
                  >
                    Encerrar Navegador
                  </Button>
                </Space>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text type="secondary">Nenhum navegador SSW ativo</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Clique em "Iniciar Navegador" para começar
                </Text>
              </div>
            )}
          </Space>
        </Card>
      </div>

      {/* Configuration Modal */}
      <Modal
        title={
          <Space>
            <DatabaseOutlined />
            <Text>Configuração de Integração SSW</Text>
          </Space>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveConfig}
          initialValues={{
            baseUrl: 'https://sistema.ssw.inf.br/bin/ssw0422',
          }}
        >
          <Form.Item
            name="dominio"
            label="Domínio"
            rules={[{ required: true, message: 'Por favor, insira o domínio' }]}
          >
            <Input
              prefix={<GlobalOutlined style={{ color: '#1890ff' }} />}
              placeholder="ex: ssw.sistema.com.br"
            />
          </Form.Item>

          <Form.Item
            name="cpf"
            label="CPF"
            rules={[{ required: true, message: 'Por favor, insira o CPF' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#52c41a' }} />}
              placeholder="000.000.000-00"
              maxLength={14}
            />
          </Form.Item>

          <Form.Item
            name="usuario"
            label="Usuário"
            rules={[{ required: true, message: 'Por favor, insira o usuário' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#1890ff' }} />}
              placeholder="login_usuario"
            />
          </Form.Item>

          <Form.Item
            name="senha"
            label="Senha"
            rules={[{
              validator: (_, value) => {
                if (!value && !form.getFieldValue('cpf')) {
                  return Promise.resolve();
                }
                if (value && value.length < 6) {
                  return Promise.reject(new Error('A senha deve ter pelo menos 6 caracteres'));
                }
                return Promise.resolve();
              },
            }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#f5222d' }} />}
              placeholder="Digite a senha (deixe em branco para manter a atual)"
            />
          </Form.Item>

          <Form.Item
            name="baseUrl"
            label="URL Base do SSW"
            rules={[{ required: true, message: 'Por favor, insira a URL base' }]}
          >
            <Input
              prefix={<DatabaseOutlined style={{ color: '#722ed1' }} />}
              placeholder="https://sistema.ssw.inf.br/bin/ssw0422"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Salvar Configuração
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SSWComponent;